/**
 * Type definitions for business services
 * Includes workplace details, form handling, and service responses
 */

import type { DateFormat, TimeFormat } from '../../utils/formatters';
import type { CountryOption } from '../../utils/countries';

/**
 * Business hours for a single day
 */
export interface DayBusinessHours {
  enabled: boolean;
  start: string; // HH:MM format
  end: string;   // HH:MM format
}

/**
 * Business hours for all days of the week
 */
export interface BusinessHours {
  monday: DayBusinessHours;
  tuesday: DayBusinessHours;
  wednesday: DayBusinessHours;
  thursday: DayBusinessHours;
  friday: DayBusinessHours;
  saturday: DayBusinessHours;
  sunday: DayBusinessHours;
}

/**
 * Database representation of workplace details
 * Matches the company_settings table structure
 */
export interface WorkplaceDetails {
  business_name: string | null;
  time_format: TimeFormat;
  date_format: DateFormat;
  country_code: string | null;
  currency_code: string | null;
  theme_color: string | null;
  logo_url: string | null;
  timezone: string;
  business_hours: BusinessHours;
  updated_at: string;
}

/**
 * Form data structure for business hours
 * Used in UI components and form handling
 */
export interface BusinessHoursForm {
  timezone: string;
  businessHours: BusinessHours;
}

/**
 * Form data structure for workplace details
 * Used in UI components and form handling
 */
export interface WorkplaceDetailsForm {
  businessName: string;
  timeFormat: TimeFormat;
  dateFormat: DateFormat;
  countryCode: string;
  currencyCode: string;
  themeColor: string;
  logoFile?: File;
}

/**
 * Partial form data for updates
 * Allows updating individual fields
 */
export interface WorkplaceDetailsUpdate {
  businessName?: string;
  timeFormat?: TimeFormat;
  dateFormat?: DateFormat;
  countryCode?: string;
  currencyCode?: string;
  themeColor?: string;
  logoFile?: File;
}

/**
 * Logo upload result from storage service
 */
export interface LogoUploadResult {
  url: string;
  path: string;
  size: number;
  contentType: string;
}

/**
 * Logo upload options and configuration
 */
export interface LogoUploadOptions {
  maxSizeBytes: number;
  allowedTypes: string[];
  quality: number;
  companyId: string;
}

/**
 * Service response wrapper for API calls
 */
export interface ServiceResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

/**
 * Workplace service error types
 */
export type WorkplaceServiceError = 
  | 'insufficient_permissions'
  | 'invalid_time_format'
  | 'invalid_date_format'
  | 'invalid_country_code'
  | 'invalid_currency_code'
  | 'invalid_theme_color'
  | 'file_too_large'
  | 'invalid_file_type'
  | 'upload_failed'
  | 'network_error'
  | 'unknown_error';

/**
 * Workplace service error details
 */
export interface WorkplaceServiceErrorDetails {
  code: WorkplaceServiceError;
  message: string;
  details?: string;
}

/**
 * Theme color preset options
 */
export interface ThemeColorPreset {
  name: string;
  color: string;
  description?: string;
}

/**
 * Available theme color presets
 */
export const THEME_COLOR_PRESETS: ThemeColorPreset[] = [
  { name: 'Blue', color: '#3B82F6', description: 'Professional blue' },
  { name: 'Green', color: '#10B981', description: 'Fresh green' },
  { name: 'Purple', color: '#8B5CF6', description: 'Creative purple' },
  { name: 'Red', color: '#EF4444', description: 'Bold red' },
  { name: 'Orange', color: '#F97316', description: 'Energetic orange' },
  { name: 'Pink', color: '#EC4899', description: 'Vibrant pink' },
  { name: 'Indigo', color: '#6366F1', description: 'Deep indigo' },
  { name: 'Teal', color: '#14B8A6', description: 'Calm teal' },
];

/**
 * Form field validation state
 */
export interface FieldValidation {
  isValid: boolean;
  error?: string;
  touched?: boolean;
}

/**
 * Complete form validation state
 */
export interface WorkplaceDetailsFormValidation {
  businessName: FieldValidation;
  timeFormat: FieldValidation;
  dateFormat: FieldValidation;
  countryCode: FieldValidation;
  currencyCode: FieldValidation;
  themeColor: FieldValidation;
  logoFile: FieldValidation;
  isFormValid: boolean;
}

/**
 * Form submission state
 */
export interface FormSubmissionState {
  isSubmitting: boolean;
  hasSubmitted: boolean;
  submitError?: string;
  submitSuccess: boolean;
}

/**
 * Logo management state
 */
export interface LogoState {
  currentLogoUrl?: string;
  previewUrl?: string;
  isUploading: boolean;
  uploadProgress?: number;
  uploadError?: string;
}

/**
 * Complete workplace details component state
 */
export interface WorkplaceDetailsState {
  formData: WorkplaceDetailsForm;
  validation: WorkplaceDetailsFormValidation;
  submission: FormSubmissionState;
  logo: LogoState;
  selectedCountry?: CountryOption;
}

/**
 * Workplace details service interface
 */
export interface WorkplaceDetailsService {
  getWorkplaceDetails(): Promise<ServiceResponse<WorkplaceDetails>>;
  updateWorkplaceDetails(data: WorkplaceDetailsUpdate): Promise<ServiceResponse<WorkplaceDetails>>;
  uploadLogo(file: File, companyId: string): Promise<ServiceResponse<LogoUploadResult>>;
  deleteLogo(): Promise<ServiceResponse<void>>;
  validateWorkplaceDetails(data: WorkplaceDetailsUpdate): Promise<ServiceResponse<boolean>>;
}

/**
 * React Query keys for workplace details
 */
export const WORKPLACE_DETAILS_QUERY_KEYS = {
  all: ['workplace-details'] as const,
  details: () => [...WORKPLACE_DETAILS_QUERY_KEYS.all, 'details'] as const,
  logo: (companyId: string) => [...WORKPLACE_DETAILS_QUERY_KEYS.all, 'logo', companyId] as const,
} as const;

/**
 * Common timezone options
 */
export const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Europe/Berlin', label: 'Central European Time (Berlin)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time' },
  { value: 'Asia/Shanghai', label: 'China Standard Time' },
  { value: 'Asia/Singapore', label: 'Singapore Time' },
  { value: 'Asia/Jakarta', label: 'Western Indonesia Time (Jakarta)' },
  { value: 'Asia/Makassar', label: 'Central Indonesia Time (Bali, Makassar)' },
  { value: 'Asia/Jayapura', label: 'Eastern Indonesia Time (Jayapura)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time' },
];

/**
 * Default business hours (Monday-Friday 9 AM to 5 PM)
 */
export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' },
};

/**
 * Default business hours form
 */
export const DEFAULT_BUSINESS_HOURS_FORM: BusinessHoursForm = {
  timezone: 'UTC',
  businessHours: DEFAULT_BUSINESS_HOURS,
};

/**
 * Default form values
 */
export const DEFAULT_WORKPLACE_DETAILS_FORM: WorkplaceDetailsForm = {
  businessName: '',
  timeFormat: '12h',
  dateFormat: 'DD/MM/YYYY',
  countryCode: '',
  currencyCode: '',
  themeColor: '#3B82F6', // Default blue
};

/**
 * Convert database workplace details to form data
 */
export const workplaceDetailsToForm = (details: WorkplaceDetails): WorkplaceDetailsForm => {
  return {
    businessName: details.business_name || '',
    timeFormat: details.time_format,
    dateFormat: details.date_format,
    countryCode: details.country_code || '',
    currencyCode: details.currency_code || '',
    themeColor: details.theme_color || '#3B82F6',
  };
};

/**
 * Convert form data to database update format
 */
export const formToWorkplaceDetailsUpdate = (form: WorkplaceDetailsForm): WorkplaceDetailsUpdate => {
  return {
    businessName: form.businessName.trim() || undefined,
    timeFormat: form.timeFormat,
    dateFormat: form.dateFormat,
    countryCode: form.countryCode || undefined,
    currencyCode: form.currencyCode || undefined,
    themeColor: form.themeColor || undefined,
    logoFile: form.logoFile,
  };
};
